/**
 * Единственный источник истины по URL педагогического раздела.
 *
 * До 21.08.2026 в разделе жили три несовместимые схемы адресов
 * (`pedagogy/teachers`, `pedagogy-specialists`, `trainer-checklist`),
 * у раздела не было корневого маршрута, а подписи экранов перечислялись руками
 * в `Header` и `appTracking` — четыре адреса там просто отсутствовали и показывались
 * как «Partners Results». Теперь пути, заголовки и имена экранов объявлены здесь,
 * а `App.tsx` / `Header` / `appTracking` / `PedagogySectionNav` читают эту таблицу.
 *
 * Правило: новый экран раздела заводится ДОБАВЛЕНИЕМ строки в PEDAGOGY_SCREENS,
 * а не ещё одним `if` в чужом файле.
 */

export const PEDAGOGY_ROOT = '/management/pedagogy'

export const pedagogyPaths = {
  root: PEDAGOGY_ROOT,
  teachers: `${PEDAGOGY_ROOT}/teachers`,
  teacherNew: `${PEDAGOGY_ROOT}/teachers/new`,
  legacyReviewMatching: `${PEDAGOGY_ROOT}/teachers/legacy-reviews`,
  teacher: (teacherId: string) => `${PEDAGOGY_ROOT}/teachers/${encodeURIComponent(teacherId)}`,
  reviews: `${PEDAGOGY_ROOT}/reviews`,
  reviewNew: `${PEDAGOGY_ROOT}/reviews/new`,
  reviewReport: (reviewId: string) => `${PEDAGOGY_ROOT}/reviews/report/${encodeURIComponent(reviewId)}`,
  queue: `${PEDAGOGY_ROOT}/queue`,
  roleRequests: `${PEDAGOGY_ROOT}/role-requests`,
  department: `${PEDAGOGY_ROOT}/department`,
  dispatch: `${PEDAGOGY_ROOT}/department/planning`,
  specialist: (specialistUserId: string) => `${PEDAGOGY_ROOT}/department/${encodeURIComponent(specialistUserId)}`,
  coverage: `${PEDAGOGY_ROOT}/department/coverage`,
  reviewDistribution: `${PEDAGOGY_ROOT}/teachers/review-distribution`,
} as const

/** Вкладки верхнего уровня раздела. Живут в layout-маршруте, а не в каждой странице. */
export const PEDAGOGY_SECTION_TABS = [
  { key: 'department', label: 'Работа отдела', path: pedagogyPaths.department },
  { key: 'teachers', label: 'Педагоги', path: pedagogyPaths.teachers },
  { key: 'reviewDistribution', label: 'Анализ отсмотров', path: pedagogyPaths.reviewDistribution },
  { key: 'reviews', label: 'Отсмотры', path: pedagogyPaths.reviews },
  { key: 'queue', label: 'Задания', path: pedagogyPaths.queue },
  { key: 'roleRequests', label: 'Старшие педагоги', path: pedagogyPaths.roleRequests },
] as const

export type PedagogySectionTabKey = typeof PEDAGOGY_SECTION_TABS[number]['key']

type PedagogyScreen = {
  /** Проверяется по pathname без завершающего слэша. */
  pattern: RegExp
  /** Заголовок в шапке приложения. */
  title: string
  /** Имя экрана в журнале посещений (`get_user_screen_log`). */
  screen: string
  /** Вкладка раздела, которую подсвечивает навигация. */
  tab: PedagogySectionTabKey | null
  /**
   * Экран без «обвязки» раздела (вкладки не рисуются).
   * Печатная форма отчёта — единственный такой случай: это полностраничный
   * белый лист под PDF, навигация приложения в нём лишняя.
   */
  chromeless?: true
}

const R = String.raw

/** Порядок значим: более специфичные шаблоны идут первыми. */
const PEDAGOGY_SCREENS: PedagogyScreen[] = [
  {
    pattern: new RegExp(R`^/management/pedagogy/teachers/review-distribution$`),
    title: 'Анализ отсмотров',
    screen: 'Анализ отсмотров',
    tab: 'reviewDistribution',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/teachers/legacy-reviews$`),
    title: 'Привязка старых отсмотров',
    screen: 'Привязка старых отсмотров',
    tab: 'teachers',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/teachers/new$`),
    title: 'Новый педагог',
    screen: 'Новый педагог',
    tab: 'teachers',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/teachers/[^/]+$`),
    title: 'Карточка педагога',
    screen: 'Карточка педагога',
    tab: 'teachers',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/teachers$`),
    title: 'Педагоги франчайзи',
    screen: 'Педагоги франчайзи',
    tab: 'teachers',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/reviews/new$`),
    title: 'Отсмотр тренировки',
    screen: 'Чеклист тренера',
    tab: 'reviews',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/reviews/report/[^/]+$`),
    title: 'Отчёт по отсмотру',
    screen: 'Чеклист тренера: отчёт',
    tab: 'reviews',
    chromeless: true,
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/reviews$`),
    title: 'Отсмотры',
    screen: 'Чеклист тренера: сводка',
    tab: 'reviews',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/queue$`),
    title: 'Задания на отсмотр',
    screen: 'Задания на отсмотр',
    tab: 'queue',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/role-requests$`),
    title: 'Запросы на повышение до старшего педагога',
    screen: 'Запросы на повышение до старшего педагога',
    tab: 'roleRequests',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/department/planning$`),
    title: 'Планирование нагрузки',
    screen: 'Планирование нагрузки педагогов',
    tab: 'department',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/department/coverage$`),
    title: 'Назначения специалистов',
    screen: 'Назначения специалистов',
    tab: 'department',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/department/[^/]+$`),
    title: 'Специалист по педагогам',
    screen: 'Специалист по педагогам',
    tab: 'department',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy/department$`),
    title: 'Работа педагогического отдела',
    screen: 'Педагогическое направление',
    tab: 'department',
  },
  {
    pattern: new RegExp(R`^/management/pedagogy$`),
    title: 'Педагогический отдел',
    screen: 'Педагогический отдел',
    tab: null,
  },
]

function normalize(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1)
  return pathname
}

function findScreen(pathname: string): PedagogyScreen | null {
  const path = normalize(pathname)
  return PEDAGOGY_SCREENS.find(screen => screen.pattern.test(path)) ?? null
}

export function isPedagogyPath(pathname: string): boolean {
  return normalize(pathname).startsWith(PEDAGOGY_ROOT)
}

/** Заголовок в шапке. `null` — путь не относится к разделу. */
export function getPedagogySectionTitle(pathname: string): string | null {
  return findScreen(pathname)?.title ?? null
}

/** Имя экрана для журнала посещений. `null` — путь не относится к разделу. */
export function getPedagogyScreenName(pathname: string): string | null {
  return findScreen(pathname)?.screen ?? null
}

/** Активная вкладка раздела для навигации. */
export function getPedagogyActiveTab(pathname: string): PedagogySectionTabKey | null {
  return findScreen(pathname)?.tab ?? null
}

/** Экран рисуется без вкладок раздела (печатная форма отчёта). */
export function isPedagogyChromelessPath(pathname: string): boolean {
  return findScreen(pathname)?.chromeless === true
}
